// Геометрия «змейки» карты курса — порт из веб-академии (`CourseProgressPath/buildRoadmapPath`),
// которая сама перенесена из TMA. Числа не менять без правки визуала плеера: карта в студии
// обязана выглядеть так же, как у ученика.

export interface RoadmapPoint {
  x: number
  y: number
}

export interface RoadmapPath {
  /** SVG-атрибут `d` для пути. */
  d: string
  /** Центры узлов уроков (по индексу урока). */
  positions: RoadmapPoint[]
  /** Высота viewBox без TOP_ROOM. */
  viewBoxHeight: number
}

/** Сторона коробки узла: запас вокруг кнопки под кольцо текущего урока. */
export const NODE_BOX = 100
export const NODE_SIZE = 54
export const NODE_INSET = (NODE_BOX - NODE_SIZE) / 2
/** Запас над первым рядом: без него верх кольца первых уроков срезается краем SVG. */
export const TOP_ROOM = 16
/** Ширина viewBox: геометрия змейки рассчитана под неё. */
export const ROADMAP_WIDTH = 320

const R = 48
const K = 26.5
const SMALL_V = 3.5
const PATTERN = [269.782, 50.218, 134.42, 50.218, 269.782, 178.34]
const START_X = 50.218
const START_Y = 30

/** Сдвиг узла вдоль его ряда по индексу урока. */
const nodeOffsetX = (i: number): number => {
  if (i === 1) return 0
  if (i % 6 === 2) return 110
  if (i % 6 === 5) return -110
  if (i % 4 === 1 || i % 4 === 3) return -35
  if (i % 6 === 4) return 50
  return 55
}

/** Направление второго поворота: чтобы горизонталь шла к концу следующего ряда. */
const chooseTurnSign = (vertX: number, nextEndX: number): number => {
  for (const sign of [1, -1]) {
    const dir = nextEndX - (vertX + sign * R)
    if (Math.sign(dir) === sign) return sign
  }
  return 1
}

/** Змейка и центры узлов. Дорожка обрывается на последнем уроке. */
export const buildRoadmapPath = (totalLessons: number): RoadmapPath => {
  const endXs = Array.from({ length: totalLessons }, (_, i) => PATTERN[i % PATTERN.length])

  const commands: string[] = [`M${START_X} ${START_Y}`]
  let rowHorizontalIndex = commands.length
  // у первого ряда два узла; курс из одного урока — только точка
  commands.push(`H${totalLessons > 1 ? endXs[0] : START_X}`)

  let currentX = endXs[0] ?? START_X
  let currentY = START_Y
  let prevSign = currentX > START_X ? 1 : -1
  const positions: RoadmapPoint[] = [{ x: START_X, y: START_Y }]

  for (let i = 1; i < totalLessons; i++) {
    positions.push({ x: currentX + nodeOffsetX(i), y: currentY })

    if (i === totalLessons - 1) {
      commands[rowHorizontalIndex] = `H${currentX + nodeOffsetX(i)}`
      break
    }

    const vertX = currentX + prevSign * R
    const vertY = currentY + R
    commands.push(
      `C${currentX + prevSign * K} ${currentY} ${vertX} ${currentY + (R - K)} ${vertX} ${vertY}`,
    )

    const vToY = vertY + SMALL_V
    commands.push(`V${vToY}`)

    const nextEndX = endXs[i]
    const sign = chooseTurnSign(vertX, nextEndX)
    const endY = vToY + R
    commands.push(
      `C${vertX} ${vToY + K} ${vertX + sign * (R - K)} ${endY} ${vertX + sign * R} ${endY}`,
    )

    rowHorizontalIndex = commands.length
    commands.push(`H${nextEndX}`)

    currentX = nextEndX
    currentY = endY
    prevSign = sign
  }

  const lowestNodeY = positions.reduce((max, pos) => Math.max(max, pos.y), 0)
  const viewBoxHeight = Math.max(currentY, lowestNodeY) + NODE_BOX / 2

  return { d: commands.join(' '), positions, viewBoxHeight }
}

// Раскладка маскотов сбоку карты — те же числа, что в плеере (.mascotItem: размер 160,
// отступ сверху 140, у первого 190).
export const MASCOT_FIRST_TOP = 190
export const MASCOT_GAP = 140
export const MASCOT_SIZE = 160

/** Высота колонки маскотов: карта не должна быть ниже неё. */
export const getMascotsColumnHeight = (count: number): number =>
  count <= 0 ? 0 : MASCOT_FIRST_TOP + count * MASCOT_SIZE + (count - 1) * MASCOT_GAP

/** Отступ карты сверху (`pt-5` у корня RoadmapPreview). */
export const ROADMAP_TOP_PADDING = 20

/** Минимальная высота карты: колонка маскотов плюс верхний отступ, иначе карта переполняется. */
export const getRoadmapMinHeight = (slots: number): number =>
  getMascotsColumnHeight(slots) + ROADMAP_TOP_PADDING

// ─── Лента для ПК: та же змейка, повёрнутая вправо ───
// Порт `buildRotatedRoadmapPath` и `getMascotSlots` из веб-академии. Числа те же.

/** Запас под плашку «Начать» снизу ленты. */
export const LABEL_ROOM = 48
/** Поля ленты сверху и снизу: туда выступают маскоты. */
export const RIBBON_PADDING = 48
const MASCOT_RIGHT_SHIFT = 185
const MASCOT_COLUMN_OVERHANG = (MASCOT_RIGHT_SHIFT + MASCOT_SIZE - ROADMAP_WIDTH) / 2
const MASCOT_CROSS_PUSH = 16
const SHIFT_X = NODE_BOX / 2 - START_Y
const MASCOT_CROSS_NEAR = MASCOT_SIZE / 2 - MASCOT_COLUMN_OVERHANG
const MASCOT_CROSS_FAR = MASCOT_CROSS_NEAR + MASCOT_RIGHT_SHIFT
const MASCOT_FIRST_LESSON = 3
const MASCOT_EVERY_LESSONS = 3

export interface RoadmapRibbon {
  d: string
  positions: RoadmapPoint[]
  height: number
  width: number
}

const transposeCommand = (letter: string, args: number[]): string => {
  if (letter === 'H') return `V${args[0]}`
  if (letter === 'V') return `H${args[0] + SHIFT_X}`
  const pairs: string[] = []
  for (let i = 0; i < args.length; i += 2) pairs.push(`${args[i + 1] + SHIFT_X} ${args[i]}`)
  return `${letter}${pairs.join(' ')}`
}

/** Путь, отражённый по диагонали: вниз становится вправо. */
const transposeRoadmapD = (d: string): string =>
  Array.from(d.matchAll(/([MHVC])([^MHVC]*)/g), ([, letter, rest]) =>
    transposeCommand(letter, rest.trim().split(/\s+/).map(Number)),
  ).join(' ')

export const buildRoadmapRibbon = (lessons: number): RoadmapRibbon => {
  const { d, positions, viewBoxHeight } = buildRoadmapPath(lessons)
  return {
    d: transposeRoadmapD(d),
    positions: positions.map(({ x, y }) => ({ x: y + SHIFT_X, y: x })),
    height: ROADMAP_WIDTH + LABEL_ROOM,
    width: viewBoxHeight + SHIFT_X,
  }
}

/** Уроков нужно, чтобы на ленте был карман маскота с этим индексом. */
const lessonsForPocket = (index: number): number =>
  MASCOT_FIRST_LESSON + index * MASCOT_EVERY_LESSONS + 2

/**
 * Центр маскота на ленте: карман между двумя уроками, по очереди сверху и снизу дорожки.
 * Лента строится на все слоты сразу: маскот сверх открытых мест (`mascots` длиннее, чем
 * позволяют уроки) должен остаться доступным, в плеере такой просто не показывается.
 */
export const ribbonMascotCenters = (slots: number): RoadmapPoint[] => {
  if (slots === 0) return []
  const { positions } = buildRoadmapRibbon(lessonsForPocket(slots - 1))
  return Array.from({ length: slots }, (_, i) => {
    const first = MASCOT_FIRST_LESSON + i * MASCOT_EVERY_LESSONS
    return {
      x: (positions[first].x + positions[first + 1].x) / 2,
      y: i % 2 === 0 ? MASCOT_CROSS_FAR + MASCOT_CROSS_PUSH : MASCOT_CROSS_NEAR - MASCOT_CROSS_PUSH,
    }
  })
}
